import { defineComponent as _defineComponent } from 'vue'
import type { Route } from 'vue-router';

import { Viewport } from '@/enums/viewport';
import { CollectionType } from '@/enums/collection-type';

import SearchBar from '@/features/search/components/SearchBar.vue';

import { getViewport } from '@/helpers/ui-helper';

import { useI18n } from '@/helpers/composables/useI18n';

import { useSearch } from '@/features/search/composables/useSearch';
import { useSearchTracking } from '@/features/search/tracking';

import { useMounted } from '@vueuse/core';
import { useRoute } from 'vue-router/composables';
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchSuggesterBar',
  props: {
    collectionType: null
  },
  emits: ["cancel"],
  setup(__props: any, { expose, emit }: { emit: ({ (e: 'cancel'): void }), expose: any, slots: any, attrs: any }) {

const props = __props as {
	collectionType: CollectionType;
};

const SearchSuggesterResults = () =>
	import(
		/* webpackChunkName: "search-suggester-results" */ '@/features/search/components/SearchSuggesterResults.vue'
	);





const {
	isSearchSuggesterOpen,
	search,
	personId,
	setSearchSuggesterVisibility,
	query: searchQuery,
	goToSearch,
	searchTitleContexts,
} = useSearch();

const { trackSearch } = useSearchTracking();

const route = useRoute();
// Intialize query and person_id from URL if params are present
onMounted(() => {
	if (route.query.q != null && searchQuery.value == '') {
		search(route.query.q.toString());
	}

	if (route.query.person_id != null && personId.value == null) {
		const person_id = Number(route.query.person_id);
		if (!isNaN(person_id)) personId.value = person_id;
	}
});

const isMounted = useMounted();

const searchbar = ref<InstanceType<typeof SearchBar> | null>(null);

const searchResults = ref<{ $children?: any[] } | null>(null);

const { tc } = useI18n();
const { activeRoute } = storeToRefs(useRoutingStore());

const placeholder = getViewport() === Viewport.DESKTOP ? tc('WEBAPP_SEARCH_PLACEHOLDER') : tc('WEBAPP_SEARCH');

watch(
	() => props.collectionType,
	newCollection => {
		if (newCollection !== CollectionType.SEARCH) emit('cancel');
	}
);

/** close results when the route has been changed. */
watch(activeRoute, (route: Route | null) => {
	closeSearchBar(true);

	// if the route has changed to a persons page, replace the input with the proper name of the person
	if (route?.query.person_id && searchbar.value?.searchBarInput) {
		search(route.query.q as string);
	}
});

const isLightTheme = computed(() => {
	return false;
	//return country.value === 'GB' && activeRoute.value.path === '/' && !isUserOnboarded.value;
});

async function openSearchBar() {
	if (isSearchSuggesterOpen.value) return;

	// INP case: artificially give the JS thread time to breathe by adding a re-render.
	await new Promise(resolve => setTimeout(resolve, 0));

	search(searchQuery.value);
	setSearchSuggesterVisibility(true);
}

function closeSearchBar(forceClose: boolean = false) {
	// if user is on search, we want to keep the search bar
	//if user navigates to title page from search, close
	if (props.collectionType !== CollectionType.SEARCH || forceClose) {
		emit('cancel');
		setSearchSuggesterVisibility(false);
	}
}

function editSearch(searchTerm?: string) {
	if (searchbar.value?.searchBarInput == null) return;

	searchbar.value?.searchBarInput?.focus();
	search(searchTerm ?? '');
}

/* KEYBOARD NAVIGATION AND FOCUS */
const index = ref(-1);

function handleSearch(query: string) {
	index.value = -1;
	search(query);
}

const searchResultsItems = computed(() => {
	// We need this computed property to listen to inputQuery.value changes so the new searchResults chidren $refs are accessed when the DOM updates
	if (!searchQuery.value) return [];

	const titleDetailLinks =
		searchResults.value?.$children?.[0]?.$children.filter((child: { $vnode: { tag: string | string[] } }) => {
			return child.$vnode.tag.includes('SearchResultTitle');
		}) ?? [];

	return titleDetailLinks;
});

function move(direction: 'up' | 'down') {
	if (direction === 'down') {
		index.value = (index.value + 1) % searchResultsItems.value.length;
	} else {
		index.value = Math.max(-1, index.value - 1);
	}

	if (index.value === -1) {
		searchbar.value?.searchBarInput?.focus();
	}

	if (searchResultsItems.value) {
		searchResultsItems.value[index.value]?.$refs['resultItem']?.focus?.();
	}
}

function navigateToSearchPage(label: 'enter' | 'show_all') {
	goToSearch({ searchString: searchQuery.value }, false);

	trackSearch('submitSearch', { spec: { label }, contexts: searchTitleContexts.value });
}

expose({
	setSearchSuggesterVisibility,
	isSearchSuggesterOpen,
});

return { __sfc: true,SearchSuggesterResults, props, emit, isSearchSuggesterOpen, search, personId, setSearchSuggesterVisibility, searchQuery, goToSearch, searchTitleContexts, trackSearch, route, isMounted, searchbar, searchResults, tc, activeRoute, placeholder, isLightTheme, openSearchBar, closeSearchBar, editSearch, index, handleSearch, searchResultsItems, move, navigateToSearchPage, SearchBar }
}

})